import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/Button'
import { Calendar } from '@/components/Calendar'
import { DateField } from '@/components/DateField'
import { LeaseTermOption } from '@/components/LeaseTermOption'
import { fetchLeaseTerms } from '@/api/fetchCollection'
import { getMaxDaysOut, getOptions } from '@/data/config'
import { leaseDebug } from '@/utils/leaseDebug'
import { toLocalIsoDate } from '@/utils/formatDate'
import type { ApiLeaseTerm, LeaseOverride } from '@/types/api'

export interface LeaseOption {
	term: string
	baseRent: string
	rentValue: number
}

interface LeaseOptionsModalProps {
	open: boolean
	onClose: () => void
	unitId: string
	availableDate: string | null
	maxLeaseStart: string | null
	unitFees: number | { min: number; max: number }
	/** The unit's base rent, used as-is when a flat-pricing asset has no lease term matrix. */
	basePrice: number | null
	initialMoveInDate?: string
	initialTerm?: number
	onConfirm: (override: LeaseOverride) => void
}

function formatCurrency (value: number): string {
	return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

/**
 * Smart price formatter matching PHP format_price() / has_decimal_greater_than_zero().
 * Shows cents only when the value has a meaningful fractional part.
 */
function formatPrice (value: number): string {
	const num = Number(value)
	const fractional = Math.abs(num) - Math.floor(Math.abs(num))
	const decimals = fractional > 0.001 ? 2 : 0
	return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

function computePriceWithFees (
	basePrice: number,
	fees: number | { min: number; max: number }
): string {
	const price = Number(basePrice)
	if (typeof fees === 'number') {
		return formatPrice(price + Number(fees))
	}
	const min = price + Number(fees.min)
	const max = price + Number(fees.max)
	if (min === max) return formatPrice(min)
	return `${formatPrice(min)} - ${formatPrice(max)}`
}

function computeMinDate (availableDate: string | null): string {
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	if (!availableDate) return toLocalIsoDate(today)
	const avail = new Date(availableDate + 'T00:00:00')
	return toLocalIsoDate(avail > today ? avail : today)
}

function computeMaxDate (maxLeaseStart: string | null): string {
	if (maxLeaseStart) return maxLeaseStart
	const max = new Date()
	max.setDate(max.getDate() + getMaxDaysOut())
	return toLocalIsoDate(max)
}

/**
 * Pick the strategy-resolved default ("Best Value") term. Prefers the term the
 * backend flagged for this date; falls back to numeric lowest price when the
 * field is absent (e.g. legacy responses or mocks).
 */
function pickBestValueTerm (terms: ApiLeaseTerm[], apiDefault?: string | null): ApiLeaseTerm | null {
	if (terms.length === 0) return null
	if (apiDefault != null) {
		// Compare as strings: term values may arrive as numbers or strings depending on the data source.
		const match = terms.find(t => String(t.lease_term) === String(apiDefault))
		if (match) return match
	}
	return terms.reduce((min, t) => Number(t.price) < Number(min.price) ? t : min, terms[0])
}

export function LeaseOptionsModal ({
	open,
	onClose,
	unitId,
	availableDate,
	maxLeaseStart,
	unitFees,
	basePrice,
	initialMoveInDate,
	initialTerm,
	onConfirm
}: LeaseOptionsModalProps) {
	const [leaseTerms, setLeaseTerms] = useState<ApiLeaseTerm[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [selectedTermValue, setSelectedTermValue] = useState<string | null>(null)
	const [defaultTermValue, setDefaultTermValue] = useState<string | null>(null)
	const [showAll, setShowAll] = useState(false)

	// Base Rent display mode and flat pricing show one base rent with no lease term choice, so the calendar only sets the move-in date.
	const opts = getOptions()
	const moveInOnly = opts.pricingDisplayMode === 'base' || opts.flatPricing

	const minDate = useMemo(() => computeMinDate(availableDate), [availableDate])
	const maxDate = useMemo(() => computeMaxDate(maxLeaseStart), [maxLeaseStart])

	const [moveInDate, setMoveInDate] = useState(initialMoveInDate ?? minDate)
	const [calMonth, setCalMonth] = useState(() => {
		const d = new Date((initialMoveInDate ?? minDate) + 'T00:00:00')
		return d.getMonth()
	})
	const [calYear, setCalYear] = useState(() => {
		const d = new Date((initialMoveInDate ?? minDate) + 'T00:00:00')
		return d.getFullYear()
	})

	const fetchIdRef = useRef(0)

	const loadTerms = useCallback(async (date: string) => {
		if (!unitId || moveInOnly) return

		const id = ++fetchIdRef.current
		setIsLoading(true)
		setError(null)

		try {
			const result = await fetchLeaseTerms(unitId, date)
			if (id !== fetchIdRef.current) return
			const terms = result.data ?? []
			setLeaseTerms(terms)

			// The backend resolves the default term (lowest price, tie-broken by strategy) for this date.
			const best = pickBestValueTerm(terms, result.default_lease_term)
			setDefaultTermValue(best?.lease_term ?? null)

			if (terms.length > 0) {
				setSelectedTermValue(prev => {
					const stillExists = terms.some(t => String(t.lease_term) === String(prev))
					return stillExists ? prev : (best?.lease_term ?? null)
				})
			} else {
				setSelectedTermValue(null)
			}
		} catch {
			if (id !== fetchIdRef.current) return
			setError('Unable to load lease terms. Please try again.')
			setLeaseTerms([])
			setSelectedTermValue(null)
		} finally {
			if (id === fetchIdRef.current) setIsLoading(false)
		}
	}, [unitId, moveInOnly])

	// Reset state when modal opens with new unit data
	const prevOpenRef = useRef(false)
	useEffect(() => {
		if (open && !prevOpenRef.current) {
			const startDate = initialMoveInDate ?? minDate
			setMoveInDate(startDate)
			const d = new Date(startDate + 'T00:00:00')
			setCalMonth(d.getMonth())
			setCalYear(d.getFullYear())
			setShowAll(false)

			if (initialTerm != null) {
				setSelectedTermValue(String(initialTerm))
			} else {
				setSelectedTermValue(null)
			}

			loadTerms(startDate)
		}
		prevOpenRef.current = open
	}, [open, initialMoveInDate, initialTerm, minDate, loadTerms])

	const handleDateChange = useCallback((newDate: string) => {
		const date = newDate || minDate
		setMoveInDate(date)
		const d = new Date(date + 'T00:00:00')
		setCalMonth(d.getMonth())
		setCalYear(d.getFullYear())
		loadTerms(date)
	}, [loadTerms, minDate])

	const bestValue = useMemo(
		() => (defaultTermValue ? leaseTerms.find(t => String(t.lease_term) === String(defaultTermValue)) ?? null : null),
		[leaseTerms, defaultTermValue]
	)

	const activeOption = leaseTerms.find(t => String(t.lease_term) === String(selectedTermValue)) ?? null

	const collapsedOptions = useMemo(() => {
		if (!activeOption || !bestValue) return leaseTerms.slice(0, 2)
		if (String(activeOption.lease_term) === String(bestValue.lease_term)) return [activeOption]
		return [activeOption, bestValue]
	}, [activeOption, bestValue, leaseTerms])

	const allOptionsSorted = useMemo(
		() => [...leaseTerms].sort((a, b) => Number(a.lease_term) - Number(b.lease_term)),
		[leaseTerms]
	)

	const visibleOptions = showAll ? allOptionsSorted : collapsedOptions

	const handleReset = () => {
		const startDate = initialMoveInDate ?? minDate
		setMoveInDate(startDate)
		const d = new Date(startDate + 'T00:00:00')
		setCalMonth(d.getMonth())
		setCalYear(d.getFullYear())
		setShowAll(false)
		if (initialTerm != null) {
			setSelectedTermValue(String(initialTerm))
		} else {
			setSelectedTermValue(null)
		}
		loadTerms(startDate)
	}

	const handleConfirm = () => {
		if (moveInOnly) {
			if (!unitId || basePrice == null || initialTerm == null) {
				leaseDebug('modal-confirm', { unitId, aborted: true, moveInOnly })
				return
			}
			const override = {
				unitId,
				selectedTerm: initialTerm,
				baseRent: basePrice,
				baseRentString: formatCurrency(basePrice),
				priceWithFeesString: computePriceWithFees(basePrice, unitFees),
				moveInDate
			}
			leaseDebug('modal-confirm', { unitId, override })
			onConfirm(override)
			return
		}
		if (!activeOption || !unitId) {
			leaseDebug('modal-confirm', { unitId, aborted: true, hasActiveOption: !!activeOption })
			return
		}
		const baseRent = activeOption.price
		const override = {
			unitId,
			selectedTerm: Number(activeOption.lease_term),
			baseRent,
			baseRentString: formatCurrency(baseRent),
			priceWithFeesString: computePriceWithFees(baseRent, unitFees),
			moveInDate
		}
		leaseDebug('modal-confirm', { unitId, override })
		onConfirm(override)
	}

	const confirmDisabled = moveInOnly
		? basePrice == null || initialTerm == null
		: isLoading || !activeOption

	const footer = (
		<>
			<Button
				label='Reset'
				appearance='ghost'
				color='content2'
				size='md'
				spacesClass='spaces-clear'
				onClick={handleReset}
			/>
			<Button
				label='Confirm'
				appearance='solid'
				color='brand'
				size='md'
				spacesClass='spaces-apply'
				onClick={handleConfirm}
				disabled={confirmDisabled}
			/>
		</>
	)

	return (
		<Modal
			open={open}
			onClose={onClose}
			title='Lease Options'
			footer={footer}
			spacesClass='spaces-lease-modal'
		>
			<div className='spaces:flex spaces:flex-col spaces:gap-24'>
				{/* Move-In Date */}
				<div className='spaces:flex spaces:flex-col spaces:gap-16'>
					<h3 className='spaces:text-16/24 spaces:font-strong spaces:text-content-1'>Move-In Date</h3>
					<Calendar
						value={moveInDate}
						onValueChange={handleDateChange}
						month={calMonth}
						year={calYear}
						onMonthChange={(m, y) => {
							setCalMonth(m)
							setCalYear(y)
						}}
						minDate={minDate}
						maxDate={maxDate}
					/>
					<DateField value={moveInDate} onValueChange={handleDateChange} />
				</div>

				{!moveInOnly && (
					<>
						{/* Divider */}
						<div className='spaces:h-px spaces:bg-canvas-3' />

						{/* Lease Term */}
						<div className='spaces:flex spaces:flex-col spaces:gap-16'>
							<h3 className='spaces:text-16/24 spaces:font-strong spaces:text-content-1'>Lease Term</h3>

							{isLoading && (
								<div className='spaces-lease-loading spaces:flex spaces:items-center spaces:justify-center spaces:py-24'>
									<span className='spaces:text-14/24 spaces:font-default spaces:text-content-3'>Loading lease terms…</span>
								</div>
							)}

							{error && !isLoading && (
								<div className='spaces-lease-error spaces:flex spaces:flex-col spaces:items-center spaces:gap-12 spaces:py-24'>
									<span className='spaces:text-14/24 spaces:font-default spaces:text-danger-content'>{error}</span>
									<Button
										label='Retry'
										appearance='subtle'
										color='brand'
										size='sm'
										onClick={() => loadTerms(moveInDate)}
									/>
								</div>
							)}

							{!isLoading && !error && leaseTerms.length === 0 && (
								<div className='spaces-lease-empty spaces:flex spaces:items-center spaces:justify-center spaces:py-24'>
									<span className='spaces:text-14/24 spaces:font-default spaces:text-content-3'>
										No lease terms available for this date.
									</span>
								</div>
							)}

							{!isLoading && !error && leaseTerms.length > 0 && (
								<div className='spaces:flex spaces:flex-col spaces:gap-8'>
									{visibleOptions.map(term => (
										<LeaseTermOption
											key={term.lease_term}
											term={`${term.lease_term} Months`}
											baseRent={formatCurrency(term.price)}
										bestValue={bestValue !== null && String(term.lease_term) === String(bestValue.lease_term)}
											selected={String(selectedTermValue) === String(term.lease_term)}
											onClick={() => {
												leaseDebug('modal-term-selected', { unitId, leaseTerm: term.lease_term, price: term.price })
												setSelectedTermValue(term.lease_term)
											}}
										/>
									))}
									{!showAll && leaseTerms.length > collapsedOptions.length && (
										<Button
											label='View All'
											appearance='subtle'
											color='content2'
											size='md'
											onClick={() => setShowAll(true)}
										/>
									)}
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</Modal>
	)
}
